import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { TrashIcon } from '@heroicons/react/24/outline';

const DevicesTested = ({
  deviceList,
  setDeviceList,
  urlID,
  ready,
  setReady,
  setShowLogBookBox,
  setShowCustomerEmailBox
}) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [expandedIndex, setExpandedIndex] = useState(null);
  const [formData, setFormData] = useState({});

  /**
   * Utility: returns the correct button color
   *  1) If current item status == this button’s action, turn it gray.
   *  2) Otherwise use the color for Y=green, N=red, NA=yellow.
   */
  const getButtonColor = (itemStatus, action) => {
    if (itemStatus === action) {
      // Once clicked, turn gray
      return 'bg-gray-600';
    }
    // Otherwise use the normal color
    switch (action) {
      case 'yes':
        return 'bg-green-500';
      case 'no':
        return 'bg-red-500';
      case 'na':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  /**
   * Toggle expansion for the device at a given index
   */
  const toggle = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  /**
   * Add/update device status (yes/no/na).
   * NOTE: We now accept `idx` so we can expand if user clicks N.
   */
  const addDevice = async (deviceKey, action, id, idx) => {
    const bodyData = {
      status: 'addDevice',
      device: deviceKey,
      action,
      id,
      inspection_id: urlID
    };

    try {
      const response = await axios.post(`${apiUrl}/contracts_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data[0] === 'success') {
        const object = response.data[1];
        const newReadyValue = response.data[2];
        const checklist = response.data[3];

        // Update 'ready' if the backend returns it
        if (setReady && newReadyValue !== undefined) {
          setReady(newReadyValue);
        }

        // Update local deviceList
        if (object?.device) {
          setDeviceList((prev) => ({
            ...prev,
            [object.device]: {
              ...prev[object.device],
              id: object.id,
              device: object.device,
              status: object.status,
              comments: object.comments || [],
              device_checklist: checklist
            }
          }));
        }

        // Only auto-expand if the user clicked "no" and device isn't "Trouble in the System?"
        if (action === 'no' && 
            deviceKey !== 'Trouble in the System?' &&
            deviceKey !== 'Central Station' 
           ) {
          setExpandedIndex(idx);
        }
      } else {
        console.error('Error adding/updating device:', response.data);
      }
    } catch (error) {
      // Handle errors
    }
  };

  /**
   * Add an issue to a device that has status = "no"
   */
  const addIssue = async (deviceKey, index) => {
    const details = formData[deviceKey] || {};
    const payload = {
      status: 'addIssue',
      device_list_id: deviceList[deviceKey].id,
      device_location: details.device_location || '',
      device_address: details.device_address || '',
      device_model: details.device_model || '',
      device_color: details.device_color || '',
      device_issue: details.device_issue || ''
    };

    try {
      const response = await axios.post(`${apiUrl}/contracts_crud.php`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.data[0] === 'success') {
        const newIssueId = response.data[1];
        const updatedReadyValue = response.data[3];

        if (setReady && updatedReadyValue !== undefined) {
          setReady(updatedReadyValue);
        }

        // Update local state
        setDeviceList((prev) => {
          const updatedIssues = prev[deviceKey].issues
            ? [...prev[deviceKey].issues]
            : [];

          updatedIssues.push({
            id: newIssueId,
            device_address: payload.device_address,
            device_location: payload.device_location,
            model: payload.device_model,
            color: payload.device_color,
            issue: payload.device_issue
          });

          return {
            ...prev,
            [deviceKey]: {
              ...prev[deviceKey],
              issues: updatedIssues
            }
          };
        });

        // Clear form data
        setFormData((prev) => ({ ...prev, [deviceKey]: {} }));
      } else {
        console.error('Error adding issue:', response.data);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error('Session expired. Please login again.', {
          position: 'top-center'
        });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: 'top-center'
        });
      } else {
        console.error('Unexpected error in addIssue:', error);
      }
    }
  };

  /**
   * Delete an existing issue by ID
   */
  const handleDelete = async (issueID) => {
    try {
      const response = await axios.post(
        `${apiUrl}/contracts_crud.php`,
        {
          status: 'delIssue',
          issue_id: issueID
        },
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.data[0] === 'success') {
        toast.success('Deleted', { position: 'top-center' });

        // Remove the deleted issue
        setDeviceList((prev) => {
          const newDeviceList = { ...prev };
          Object.keys(newDeviceList).forEach((dKey) => {
            const devItem = newDeviceList[dKey];
            if (devItem.issues) {
              newDeviceList[dKey] = {
                ...devItem,
                issues: devItem.issues.filter((iss) => iss.id !== issueID)
              };
            }
          });
          return newDeviceList;
        });
      } else {
        toast.error(response.data[1] || 'Failed to delete.', {
          position: 'top-center'
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        toast.error('Session expired. Please login again.', {
          position: 'top-center'
        });
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: 'top-center'
        });
      } else {
        console.error('Unexpected error in handleDelete:', error);
      }
    }
  };

  return (
    <div className="mb-5">
      {Object.keys(deviceList).map((deviceKey, index) => {
        const item = deviceList[deviceKey];

        return (
          <div key={deviceKey} className="border-b py-2">
            {/* Device row: Yes/No/NA Buttons */}
            <div className="flex items-center space-x-2">
              {/* YES BUTTON */}
              <button
                className={`rounded-full w-8 h-8 text-white
                  ${getButtonColor(item.status, deviceKey === 'Trouble in the System?' ? 'no' : 'yes')}`}
                onClick={() => addDevice(deviceKey, deviceKey === 'Trouble in the System?' ? 'no' : 'yes', item.id, index)}
                disabled={item.status === (deviceKey === 'Trouble in the System?' ? 'no' : 'yes')}
              >
                Y
              </button>

              {/* NO BUTTON */}
              <button
                className={`rounded-full w-8 h-8 text-white
                  ${getButtonColor(item.status, deviceKey === 'Trouble in the System?' ? 'yes' : 'no')}`}
                onClick={() => addDevice(deviceKey, deviceKey === 'Trouble in the System?' ? 'yes' : 'no', item.id, index)}
                disabled={item.status === (deviceKey === 'Trouble in the System?' ? 'yes' : 'no')}
              >
                N
              </button>

              {/* N/A BUTTON - Conditionally Rendered */}
              {deviceKey !== 'Trouble in the System?' && (
                <button
                  className={`rounded-full px-2 py-1 text-white
                    ${getButtonColor(item.status, 'na')}`}
                  onClick={() => addDevice(deviceKey, 'na', item.id, index)}
                  disabled={item.status === 'na'}
                >
                  N/A
                </button>
              )}

              {/* Expand/Collapse */}
              <span className="cursor-pointer" onClick={() => toggle(index)}>
                {deviceKey}
                {expandedIndex === index ? ' ▼' : ' ►'}
              </span>

              {/* Visual check if an item has a status */}
              {item.status && <span className="text-green-500 text-sm ml-2">✔</span>}
            </div>

            {/* LIST OF ISSUES (if expanded) */}
            {expandedIndex === index && item.issues && item.issues.length > 0 && (
              <ul className="ml-10 mt-2 text-sm">
                {item.issues.map((iss) => (
                  <li
                    key={iss.id} // each issue needs a unique key
                    className="flex justify-between mt-3"
                  >
                    <span
                      className="text-red-500 cursor-pointer"
                      onClick={() => handleDelete(iss.id)}
                      title="Delete Issue"
                    >
                      <TrashIcon className="h-5" />
                    </span>
                    <span>
                      {iss.device_address} | {iss.device_location} | {iss.color} | {iss.issue}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* ISSUE FORM if status='no' and expanded */}
            {expandedIndex === index && item.status === 'no' && (
              <div className="relative z-50 bg-yellow-100 p-3">
                <div className="mb-2 space-y-1">
                  <input
                    type="text"
                    className="border p-1 w-full"
                    placeholder="DEVICE LOCATION *"
                    required // make it mandatory
                    value={formData[deviceKey]?.device_location || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [deviceKey]: { ...prev[deviceKey], device_location: e.target.value }
                      }))
                    }
                  />

                  <input
                    type="text"
                    className="border p-1 w-full"
                    placeholder="DEVICE ADDRESS *"
                    required // make it mandatory
                    value={formData[deviceKey]?.device_address || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [deviceKey]: { ...prev[deviceKey], device_address: e.target.value }
                      }))
                    }
                  />

                  <input
                    type="text"
                    className="border p-1 w-full"
                    placeholder="DEVICE MODEL "
                     // make it mandatory
                    value={formData[deviceKey]?.device_model || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [deviceKey]: { ...prev[deviceKey], device_model: e.target.value }
                      }))
                    }
                  />

                  <select
                    className="border p-1 w-full"
                    required // mandatory
                    value={formData[deviceKey]?.device_color || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [deviceKey]: { ...prev[deviceKey], device_color: e.target.value }
                      }))
                    }
                  >
                    <option value="">SELECT COLOR *</option>
                    <option value="RED">RED</option>
                    <option value="WHITE">WHITE</option>
                  </select>

                  <select
                    className="border p-1 w-full"
                    required // mandatory
                    value={formData[deviceKey]?.device_issue || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [deviceKey]: { ...prev[deviceKey], device_issue: e.target.value }
                      }))
                    }
                  >
                    <option value="">SELECT ISSUE *</option>
                    <option value="NOT WORKING">NOT WORKING</option>
                    <option value="NOT ACTIVATING">NOT ACTIVATING</option>
                    <option value="TIME DELAY">TIME DELAY</option>
                    <option value="ACTIVE TROUBLE">ACTIVE TROUBLE</option>
                    <option value="PROGRAMMING">PROGRAMMING</option>
                    <option value="NEED ADJUSTMENT">NEED ADJUSTMENT</option>
                  </select>

                  <button
                    className="bg-blue-500 text-white py-1 px-3 rounded text-sm mt-2"
                    onClick={() => addIssue(deviceKey, index)}
                  >
                    Add Issue
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default DevicesTested;
